import React, { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { ArrowRight, Star } from 'lucide-react';
import { useApp } from '../contexts/AppContext';
import ProductCard from '../components/products/ProductCard';

const SectionHeader = ({ label, title }) => (
  <div className="text-center mb-12">
    {label && <span className="text-xs tracking-[0.3em] text-[#C4A265] uppercase font-medium">{label}</span>}
    <h2 className="text-3xl md:text-4xl font-serif text-[#3E2723] mt-2">{title}</h2>
    <div className="w-12 h-px bg-[#C4A265] mx-auto mt-4"></div>
  </div>
);

const Home = () => {
  const { products, fetchProducts } = useApp();
  const [allProducts, setAllProducts] = useState([]);

  useEffect(() => {
    fetchProducts();
  }, []);

  useEffect(() => {
    if (products.length > 0) setAllProducts(products);
  }, [products]);

  const featuredProducts = allProducts.filter(p => p.featured).slice(0, 4);
  const newArrivals = allProducts.filter(p => p.new_arrival).slice(0, 4);

  const categories = [
    { name: 'Cushions', slug: 'Cushion Covers', image: 'https://customer-assets.emergentagent.com/job_692ba4b5-e604-41da-98a1-0317aa70e185/artifacts/kz8cd2dk_4DB2C6CF-A0B3-4524-A127-E2EC9439E37E.png' },
    { name: 'Table Linens', slug: 'Table Runners', image: 'https://customer-assets.emergentagent.com/job_692ba4b5-e604-41da-98a1-0317aa70e185/artifacts/7m9zq332_B17B3C73-C7EA-4FA3-8EBD-A56461D74375.png' },
    { name: 'Home Decor', slug: 'Wall Hangings', image: 'https://customer-assets.emergentagent.com/job_692ba4b5-e604-41da-98a1-0317aa70e185/artifacts/xuj1plps_BA15CBC9-69E9-4C34-967B-A8B8CFE339E6.png' }
  ];

  const reviews = [
    { name: 'Priya S.', location: 'Mumbai', rating: 5, text: 'Absolutely stunning craftsmanship. The table runner exceeded all my expectations.' },
    { name: 'Anita K.', location: 'Delhi', rating: 5, text: 'Behat House pieces have transformed my living room. The quality is unmatched.' },
    { name: 'Meera R.', location: 'Bangalore', rating: 5, text: 'Custom cushion covers arrived perfectly made. True artisan quality.' }
  ];

  return (
    <div className="min-h-screen bg-[#FEFCF9]">

      {/* ── Hero ── */}
      <section className="relative overflow-hidden">
        <Link to="/shop" className="block" data-testid="shop-now-button">
          <img
            src="https://customer-assets.emergentagent.com/job_692ba4b5-e604-41da-98a1-0317aa70e185/artifacts/wl55spom_E754F8F1-7AAB-4743-8824-8E7B74916CEB.png"
            alt="Behat House - Timeless Elegance - Handcrafted Home Decor"
            className="w-full h-auto object-cover"
          />
        </Link>
      </section>

      {/* ── New Arrivals ── */}
      <section className="py-12 sm:py-16 lg:py-20 bg-[#FEFCF9]">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <SectionHeader label="Just In" title="New Arrivals" />
          {newArrivals.length > 0 ? (
            <div className="grid grid-cols-2 lg:grid-cols-4 gap-4 sm:gap-6 lg:gap-8 mb-10 sm:mb-12">
              {newArrivals.map((product) => (
                <ProductCard key={product.id} product={product} />
              ))}
            </div>
          ) : (
            <div className="text-center py-10"><p className="text-[#A09486]">Loading arrivals...</p></div>
          )}
          <div className="text-center">
            <Link to="/shop" className="inline-flex items-center space-x-2 text-[#3E2723] tracking-[0.15em] uppercase text-sm font-medium border-b border-[#C4A265] pb-1 hover:text-[#C4A265] transition-colors duration-300 group" data-testid="explore-new-arrivals">
              <span>Explore</span>
              <ArrowRight className="w-4 h-4 group-hover:translate-x-1 transition-transform" />
            </Link>
          </div>
        </div>
      </section>

      {/* ── Categories: Cushions / Table Linens / Home Decor ── */}
      <section className="py-12 sm:py-16 lg:py-20 bg-[#F5EFE6]">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <SectionHeader label="Shop by Category" title="Explore Our Range" />
          <div className="grid grid-cols-1 sm:grid-cols-3 gap-4 sm:gap-5 lg:gap-6">
            {categories.map((cat) => (
              <Link
                key={cat.name}
                to={`/shop?category=${encodeURIComponent(cat.slug)}`}
                className="group block overflow-hidden aspect-[3/4]"
                data-testid={`category-${cat.name.toLowerCase().replace(' ', '-')}`}
              >
                <img src={cat.image} alt={cat.name} className="w-full h-full object-cover transition-transform duration-700 group-hover:scale-[1.02]" />
              </Link>
            ))}
          </div>
        </div>
      </section>

      {/* ── Featured Collection ── */}
      <section className="py-20 bg-[#FEFCF9]">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <SectionHeader label="Our Picks" title="Featured Collection" />
          {featuredProducts.length > 0 ? (
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-8 mb-12">
              {featuredProducts.map((product) => (
                <ProductCard key={product.id} product={product} />
              ))}
            </div>
          ) : (
            <div className="text-center py-10"><p className="text-[#A09486]">Loading collection...</p></div>
          )}
          <div className="text-center">
            <Link to="/shop" className="inline-flex items-center space-x-2 text-[#3E2723] tracking-[0.15em] uppercase text-sm font-medium border-b border-[#C4A265] pb-1 hover:text-[#C4A265] transition-colors duration-300 group" data-testid="explore-featured">
              <span>Explore</span>
              <ArrowRight className="w-4 h-4 group-hover:translate-x-1 transition-transform" />
            </Link>
          </div>
        </div>
      </section>

      {/* ── Customer Feedback ── */}
      <section className="py-20 bg-[#F5EFE6]">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <SectionHeader label="Testimonials" title="Customer Feedback" />
          <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
            {reviews.map((review, index) => (
              <div key={index} className="bg-[#FEFCF9] p-5 sm:p-6 lg:p-8 border border-[#E8DCC8] hover:border-[#C4A265]/40 transition-colors duration-300">
                <div className="flex items-center gap-1 mb-4">
                  {[...Array(review.rating)].map((_, i) => (
                    <Star key={i} className="w-3.5 h-3.5 text-[#C4A265] fill-[#C4A265]" />
                  ))}
                </div>
                <p className="text-[#5C5549] leading-relaxed mb-6 italic font-serif text-base">"{review.text}"</p>
                <div className="flex items-center">
                  <div className="w-10 h-10 bg-[#F5EFE6] border border-[#E8DCC8] rounded-full flex items-center justify-center text-[#C4A265] font-serif text-base">
                    {review.name.charAt(0)}
                  </div>
                  <div className="ml-3">
                    <p className="font-medium text-[#3E2723] text-sm">{review.name}</p>
                    <p className="text-xs text-[#A09486]">{review.location}</p>
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* ── Closing CTA + Brand Quote ── */}
      <section className="py-12 sm:py-16 lg:py-20 bg-[#FEFCF9] border-t border-[#E8DCC8]">
        <div className="max-w-3xl mx-auto px-4 sm:px-6 text-center">
          <p className="text-xs sm:text-sm tracking-[0.3em] text-[#C4A265] uppercase mb-3">Behat House Atelier</p>
          <h2 className="text-2xl sm:text-3xl md:text-4xl font-serif text-[#3E2723] mb-4 sm:mb-6">Bring Heritage Home</h2>
          <p className="text-sm sm:text-base text-[#7A6B5D] mb-8 sm:mb-10 font-light leading-relaxed italic font-serif">
            "Behat House Atelier is a whimsical realm where traditional craftsmanship intertwines with contemporary design. 
            With a commitment to revitalizing and conserving heritage, it pays homage to the grandeur and allure of Hindustan. 
            Each creation is a tribute to the past, weaving together stories of splendor and craftsmanship in a tapestry of wonder."
          </p>
          <Link
            to="/shop"
            className="inline-flex items-center space-x-2 bg-[#3E2723] text-[#FEFCF9] px-10 py-3.5 tracking-[0.15em] uppercase text-sm font-medium hover:bg-[#C4A265] transition-colors duration-300"
          >
            <span>Discover More</span>
            <ArrowRight className="w-4 h-4" />
          </Link>
        </div>
      </section>

      {/* WhatsApp */}
      <a
        href="https://wa.me/918700465253?text=Hi%20Behat%20House!%20I%27m%20interested%20in%20your%20handcrafted%20products."
        target="_blank"
        rel="noopener noreferrer"
        className="fixed bottom-6 right-6 z-50 bg-green-500 text-white p-4 rounded-full shadow-2xl hover:bg-green-600 hover:scale-110 transition-all"
        data-testid="whatsapp-button"
        title="Chat with us on WhatsApp"
      >
        <svg className="w-7 h-7" fill="currentColor" viewBox="0 0 24 24">
          <path d="M17.472 14.382c-.297-.149-1.758-.867-2.03-.967-.273-.099-.471-.148-.67.15-.197.297-.767.966-.94 1.164-.173.199-.347.223-.644.075-.297-.15-1.255-.463-2.39-1.475-.883-.788-1.48-1.761-1.653-2.059-.173-.297-.018-.458.13-.606.134-.133.298-.347.446-.52.149-.174.198-.298.298-.497.099-.198.05-.371-.025-.52-.075-.149-.669-1.612-.916-2.207-.242-.579-.487-.5-.669-.51-.173-.008-.371-.01-.57-.01-.198 0-.52.074-.792.372-.272.297-1.04 1.016-1.04 2.479 0 1.462 1.065 2.875 1.213 3.074.149.198 2.096 3.2 5.077 4.487.709.306 1.262.489 1.694.625.712.227 1.36.195 1.871.118.571-.085 1.758-.719 2.006-1.413.248-.694.248-1.289.173-1.413-.074-.124-.272-.198-.57-.347m-5.421 7.403h-.004a9.87 9.87 0 01-5.031-1.378l-.361-.214-3.741.982.998-3.648-.235-.374a9.86 9.86 0 01-1.51-5.26c.001-5.45 4.436-9.884 9.888-9.884 2.64 0 5.122 1.03 6.988 2.898a9.825 9.825 0 012.893 6.994c-.003 5.45-4.437 9.884-9.885 9.884m8.413-18.297A11.815 11.815 0 0012.05 0C5.495 0 .16 5.335.157 11.892c0 2.096.547 4.142 1.588 5.945L.057 24l6.305-1.654a11.882 11.882 0 005.683 1.448h.005c6.554 0 11.89-5.335 11.893-11.893a11.821 11.821 0 00-3.48-8.413z"/>
        </svg>
      </a>
    </div>
  );
};

export default Home;
